import React, { useContext, useState, useReducer, useEffect } from 'react';
import { useDebounce } from 'usehooks-ts';
import {
  Typography,
  Stack,
  Grid,
  Autocomplete,
  TextField,
  CircularProgress
} from '@mui/material';
import {
  InputButton,
  InputCheckbox,
  InputTextField
} from '@abbott-components/core';
import { useSnackbar } from 'notistack';
import {
  duplicateWorkflowReducer,
  initialState
} from './duplicateWorkflowReducer';
import { get, post } from '../Shared/ajax';
import LandingHeader from '../Landing/LandingHeader';
import SearchIcon from '@mui/icons-material/Search';
import BackToLandingButton from '../Shared/BackToLandingButton';
import { AppContext } from '../App/appReducer';
import { AppActionKind, AppPageKind } from '../App/types';
import {
  WorkflowService,
  WorkflowServiceList,
  WorkflowTypes
} from '../NewWorkflow/newWorkflowReducer';
import {
  DuplicateWorkflowActionKind,
  DuplicateWorkflowProps,
  DuplicateWorkflowRequest,
  DuplicateWorkflowResponse,
  SelectedForm
} from './types';

export default ({
  workflowId,
  workflowName,
  workflowType
}: DuplicateWorkflowProps) => {
  const { enqueueSnackbar } = useSnackbar();

  const { dispatch: appDispatch } = useContext(AppContext);

  const [duplicateWorkflowState, duplicateWorkflowDispatch] = useReducer(
    duplicateWorkflowReducer,
    { ...initialState, workflowType }
  );

  const {
    forms,
    isLoading,
    newWorkflowName,
    hasError,
    errorMessage,
    selectedForm,
    selectedId
  } = duplicateWorkflowState;

  const [inputAutocomplete, setInputAutocomplete] = useState<string>('');
  const [copyMapperSettings, setCopyMapperSettings] = useState<boolean>(true);

  const debouncedSearchTerm = useDebounce<string>(inputAutocomplete, 300);

  const showLoading = () => {
    duplicateWorkflowDispatch({
      type: DuplicateWorkflowActionKind.SET_ISLOADING,
      payload: true
    });
  };

  const hideLoading = () => {
    duplicateWorkflowDispatch({
      type: DuplicateWorkflowActionKind.SET_ISLOADING,
      payload: false
    });
  };

  const setWorkflowServices = (services: WorkflowService[]) => {
    duplicateWorkflowDispatch({
      type: DuplicateWorkflowActionKind.SET_FORMS,
      payload: services
    });
  };

  const showError = () => {
    duplicateWorkflowDispatch({
      type: DuplicateWorkflowActionKind.SET_ERROR,
      payload: true
    });
  };

  const hideError = () => {
    duplicateWorkflowDispatch({
      type: DuplicateWorkflowActionKind.SET_ERROR,
      payload: false
    });
  };

  const fetchOHSFormsByNameAsync = async (searchTerm?: string) => {
    if (!searchTerm && workflowType === WorkflowTypes.OHS) {
      searchTerm = 'form'; // default search criteria for OHS workflow type
    } else if (!searchTerm || searchTerm.length < 3) {
      return;
    }

    try {
      showLoading();
      const response = await get<WorkflowServiceList>(
        `DynamicWorkflowService?DynamicWorkflowServiceName=${searchTerm}&WorkflowType=OHS`
      );
      setWorkflowServices(response.dynamicWorkflowServices);
    } catch (err) {
      enqueueSnackbar('An unexpected error occurred retrieving workflows', {
        variant: 'error'
      });
    }
    hideLoading();
  };

  const handleWorkflowName = async (text: string) => {
    duplicateWorkflowDispatch({
      type: DuplicateWorkflowActionKind.SET_NEW_WORKFLOW_NAME,
      payload: text
    });
  };

  const setSelectedForm = (id: string, value: string) => {
    duplicateWorkflowDispatch({
      type: DuplicateWorkflowActionKind.SET_SELECTED_FORM,
      payload: { id, value } as SelectedForm
    });
  };

  const handleSelectedOptionAutocomplete = (
    event: React.SyntheticEvent<Element, Event>,
    value: string | null
  ) => {
    for (const form of forms) {
      if (form.serviceName === value) {
        setSelectedForm(form.serviceId, value);
        break;
      }
    }
  };

  const handleInputChangeAutocomplete = (
    event: React.SyntheticEvent<Element, Event>,
    input: string
  ) => {
    setInputAutocomplete(input);
  };

  const renderOHSFormsAutoComplete = () => {
    return (
      <Stack spacing={1}>
        <Stack>
          <Autocomplete
            value={selectedForm}
            onChange={handleSelectedOptionAutocomplete}
            inputValue={inputAutocomplete}
            onInputChange={handleInputChangeAutocomplete}
            loading={isLoading}
            options={forms.map((form: WorkflowService) => form.serviceName)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={'Find another form'}
                error={hasError}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: <SearchIcon />,
                  endAdornment: (
                    <>
                      {isLoading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
          />
        </Stack>
        {hasError && (
          <Stack>
            <Typography
              variant="body1"
              sx={{ fontSize: '14px', color: '#e4002b', marginLeft: '5px' }}
            >
              {errorMessage}
            </Typography>
          </Stack>
        )}
      </Stack>
    );
  };

  const handleOHSContinue = () => {
    if (selectedForm?.length === 0 || selectedForm === null) {
      showError();
    } else {
      hideError();
      postDuplicateWorkflow(workflowId, selectedForm, WorkflowTypes.OHS);
    }
  };

  const renderOtherWorkflowName = () => {
    return (
      <InputTextField
        name="textfieldOtherWorkflow"
        label="Add a Workflow Name"
        value={newWorkflowName}
        onTextChange={handleWorkflowName}
        errorText={hasError ? errorMessage : undefined}
      />
    );
  };

  const handleOtherContinue = () => {
    if (newWorkflowName.length === 0) {
      showError();
    } else {
      hideError();
      postDuplicateWorkflow(workflowId, newWorkflowName, WorkflowTypes.OWT);
    }
  };

  const workflowTypeDictionary: Record<
    WorkflowTypes,
    { field: JSX.Element; onContinue: () => void }
  > = {
    OHS: {
      field: renderOHSFormsAutoComplete(),
      onContinue: handleOHSContinue
    },
    Other: {
      field: renderOtherWorkflowName(),
      onContinue: handleOtherContinue
    }
  };

  const postDuplicateWorkflow = async (
    workflowId: string,
    workflowName: string,
    type: string
  ) => {
    try {
      const response = await post<
        DuplicateWorkflowResponse,
        DuplicateWorkflowRequest
      >(`DynamicWorkflow/${workflowId}/Duplicate`, {
        name: workflowName,
        workflowType: workflowType,
        serviceId: type === WorkflowTypes.OHS ? selectedId : '',
        copyMapperSettings
      });

      appDispatch({
        type: AppActionKind.UPDATE_WORKFLOWID,
        payload: response.id as string
      });

      appDispatch({
        type: AppActionKind.SET_APPPAGE,
        payload: AppPageKind.FORM_BUILDER
      });
    } catch (err) {
      const error = err as unknown as Error;

      duplicateWorkflowDispatch({
        type: DuplicateWorkflowActionKind.SET_ERROR,
        payload: true
      });

      duplicateWorkflowDispatch({
        type: DuplicateWorkflowActionKind.SET_ERROR_MESSAGE,
        payload: error.message
      });
    }
  };

  const handleCopyMapperSettingsChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    setCopyMapperSettings(checked);
  };

  useEffect(() => {
    fetchOHSFormsByNameAsync();
  }, []);

  useEffect(() => {
    fetchOHSFormsByNameAsync(debouncedSearchTerm);
  }, [debouncedSearchTerm]);

  return (
    <Stack>
      <LandingHeader />
      <Stack sx={{ my: 0, textAlign: 'left', margin: '90px 30px 30px 30px' }}>
        <Grid container>
          <Grid item xs={6}>
            <BackToLandingButton />
          </Grid>
        </Grid>
      </Stack>

      <Stack sx={{ my: 2, textAlign: 'center' }}>
        <Typography variant="h1">Duplicate Workflow</Typography>
        <Typography variant="subtitle1">
          {workflowName} - ({workflowType})
        </Typography>
      </Stack>

      <Stack alignItems="center">
        <Grid
          container
          direction="row"
          justifyContent="center"
          alignItems="center"
          spacing={1}
        >
          <Grid item xs={7}>
            {workflowTypeDictionary[workflowType].field}
          </Grid>
          <Grid item xs={7}>
            <InputCheckbox
              name="copyMapperSettingsCheckbox"
              defaultChecked={copyMapperSettings}
              onCheckboxChange={handleCopyMapperSettingsChange}
            >
              Copy Mapper settings
            </InputCheckbox>
          </Grid>
          <Grid container item xs={7} justifyContent="center">
            <Grid item>
              <InputButton
                type="button"
                name="continueDuplication"
                disabled={
                  selectedForm === null && newWorkflowName.length === 0
                    ? true
                    : false
                }
                onClick={workflowTypeDictionary[workflowType].onContinue}
              >
                Continue
              </InputButton>
            </Grid>
          </Grid>
        </Grid>
      </Stack>
    </Stack>
  );
};
